import React, { useState } from 'react';

const PROMPT_SECTIONS = [
  {
    id: 'arriving',
    icon: '🗺️',
    title: 'When we arrive somewhere new',
    color: 'theme-slate',
    border: 'border-l-theme-slate',
    bg: 'bg-theme-slate/10',
    text: 'text-theme-slate',
    prompts: [
      'What feels familiar here?',
      'What feels strange here?',
      'Who looks like they need help?',
      'What sound stands out first?',
      'What here reminds you of home?',
      'What here makes you slow down?',
      'What part of this place feels older than the rest?',
    ],
  },
  {
    id: 'earn-token',
    icon: '✦',
    title: 'When you want a token',
    color: 'theme-honey',
    border: 'border-l-theme-honey',
    bg: 'bg-theme-honey/10',
    text: 'text-theme-honey',
    prompts: [
      'I set aside a small offering for the local god.',
      'Give me a moment, I need to rest.',
      'Truthfully, I\'m more shaken than I look.',
      'I\'ll help, even if doing so slows me down.',
      'Look at the scale of this place.',
      'Wait, did you all see that tiny beautiful thing?',
      'None of us have seen anything like this before. What does each of us notice?',
    ],
  },
  {
    id: 'spend-token',
    icon: '🕯️',
    title: 'When you spend a token',
    color: 'theme-plum',
    border: 'border-l-theme-plum',
    bg: 'bg-theme-plum/10',
    text: 'text-theme-plum',
    prompts: [
      'I know a way through this immediate problem.',
      'Let me ease some of that pain for a moment.',
      'Stay close, I\'ll keep you safe.',
      'Sit with me for a bit.',
      'I think this person needs something deeper than they\'re saying.',
      'There\'s something hidden here. What is it?',
      'Here\'s what matters about this place.',
      'The forgotten gods know something. What do they whisper?',
    ],
  },
  {
    id: 'open-scene',
    icon: '🌿',
    title: 'Gentle ways to open a scene',
    color: 'theme-moss',
    border: 'border-l-theme-moss',
    bg: 'bg-theme-moss/10',
    text: 'text-theme-moss',
    prompts: [
      'I sit nearby and wait.',
      'I start helping with the smallest task.',
      'I ask for news of the road.',
      'I share food or water.',
      'I point out something beautiful.',
      'I ask who lives here and who left.',
      'I tell a short story from earlier on the journey.',
    ],
  },
  {
    id: 'traveler',
    icon: '👣',
    title: 'Questions for another traveler',
    color: 'theme-slate',
    border: 'border-l-theme-slate',
    bg: 'bg-theme-slate/10',
    text: 'text-theme-slate',
    prompts: [
      'What are you carrying today?',
      'What do you miss right now?',
      'What part of home still follows you?',
      'What are you trying not to think about?',
      'What do you hope waits farther down the road?',
      'What part of this place unsettles you?',
      'Do you want company, advice, or quiet?',
    ],
  },
  {
    id: 'kith',
    icon: '🐾',
    title: 'Questions for a kith',
    color: 'theme-moss',
    border: 'border-l-theme-moss',
    bg: 'bg-theme-moss/10',
    text: 'text-theme-moss',
    prompts: [
      'What do you need right now?',
      'Who are you worried about?',
      'What happened here before we arrived?',
      'What do folks get wrong about you?',
      'What are you trying to protect?',
      'Who taught you how to do that?',
      'What would make today easier?',
    ],
  },
  {
    id: 'quiet',
    icon: '🤫',
    title: 'When the table goes quiet',
    color: 'theme-rose',
    border: 'border-l-theme-rose',
    bg: 'bg-theme-rose/10',
    text: 'text-theme-rose',
    prompts: [
      'I notice one detail nobody has named yet.',
      'I ask the next soft question.',
      'I help with the work already happening.',
      'I ask the place for guidance.',
      'I tell the truth about how I feel.',
      'I ask who has not spoken yet.',
      'I describe one small act of care.',
    ],
  },
  {
    id: 'care',
    icon: '🤝',
    title: 'Good lines for rest, care, and connection',
    color: 'theme-plum',
    border: 'border-l-theme-plum',
    bg: 'bg-theme-plum/10',
    text: 'text-theme-plum',
    prompts: [
      'You don\'t have to carry that alone right now.',
      'I\'m here with you.',
      'We don\'t need to rush this moment.',
      'Eat something first, then we\'ll talk.',
      'Tell me what hurts.',
      'Tell me what feels safe.',
      'You can stay by the fire with me.',
    ],
  },
  {
    id: 'shrines',
    icon: '⛩️',
    title: 'Good lines for roads, shrines, and small gods',
    color: 'theme-honey',
    border: 'border-l-theme-honey',
    bg: 'bg-theme-honey/10',
    text: 'text-theme-honey',
    prompts: [
      'Someone has been tending this shrine.',
      'I leave something behind here.',
      'This road remembers more than people think.',
      'I ask the old gods for a sign.',
      'I think this place wants something from us.',
      'I think somebody forgot to say thank you here.',
      'What sort of god watches over a place like this?',
    ],
  },
  {
    id: 'wonder',
    icon: '✨',
    title: 'Good lines for beauty and wonder',
    color: 'theme-moss',
    border: 'border-l-theme-moss',
    bg: 'bg-theme-moss/10',
    text: 'text-theme-moss',
    prompts: [
      'The light here feels different.',
      'Listen to how the wind moves through this place.',
      'Even the quiet here feels alive.',
      'I\'ve never seen colors like this before.',
      'Something tiny here matters.',
      'For a second, the whole world feels gentle.',
      'I want to remember this part.',
    ],
  },
];

function PromptCard({ section, copied, onCopy }) {
  return (
    <div className={`bg-parchment-mid border-[1.5px] border-parchment-dark border-l-[3px] ${section.border} rounded-md p-5 shadow-card`}>
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-dashed border-parchment-dark">
        <span className="text-xl">{section.icon}</span>
        <h3 className="font-playfair text-base font-bold text-ink leading-snug">{section.title}</h3>
      </div>
      <ul className="space-y-2">
        {section.prompts.map((prompt, i) => (
          <li key={i}>
            <button
              onClick={() => onCopy(prompt)}
              className={`w-full text-left font-lora text-[0.83rem] leading-snug px-3 py-2 rounded transition-all group flex items-start gap-2
                ${copied === prompt
                  ? `${section.bg} ${section.text} font-medium`
                  : 'text-ink-mid hover:bg-parchment-dark/30 hover:text-ink'
                }`}
            >
              <span className={`mt-[2px] text-[0.7rem] flex-shrink-0 transition-opacity ${copied === prompt ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'} ${section.text}`}>✦</span>
              <span>{prompt}</span>
              {copied === prompt && (
                <span className={`ml-auto flex-shrink-0 text-[0.72rem] font-lora italic ${section.text}`}>copied!</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PromptsTab() {
  const [copied, setCopied] = useState(null);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(text);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="pl-4">
      <div className="mb-6">
        <h2 className="font-playfair text-2xl font-bold text-ink">Prompts</h2>
        <p className="font-lora text-[0.95rem] text-ink-light mt-1">
          Lines and questions to keep the journey moving. Click any prompt to copy it.
        </p>
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-5">
        {PROMPT_SECTIONS.map(section => (
          <PromptCard
            key={section.id}
            section={section}
            copied={copied}
            onCopy={handleCopy}
          />
        ))}
      </div>
    </div>
  );
}